/** 2GIS Suggest for the location panel. The key is the shared demo key (~1000 requests in total),
 * so every request is rationed: 3+ characters, debounce, a minimum gap, abort of stale ones, cache. */
export const SUGGEST_URL = 'https://catalog.api.2gis.com/3.0/suggests';
export const MIN_QUERY = 3;
export const DEBOUNCE_MS = 400;
export const MIN_GAP_MS = 1000;
/** Saint Petersburg centre as lon,lat: ranks nearby places first. */
const REGION = '30.3158,59.9343';

export type Suggestion = { id: string; title: string; subtitle: string; lat: number; lon: number };

export function suggestKey(): string {
  return import.meta.env.VITE_PP_DGIS_MAPGL_KEY ?? '';
}

export function suggestUrl(query: string, key: string): string {
  const params = new URLSearchParams({ q: query, location: REGION, locale: 'ru_RU', fields: 'items.point', key });
  return `${SUGGEST_URL}?${params}`;
}

/** Keep only items that carry a point: rubrics and query hints have none and are not re-requested. */
export function parseSuggestions(body: unknown): Suggestion[] {
  const items = (body as { result?: { items?: unknown } } | null)?.result?.items;
  if (!Array.isArray(items)) return [];
  const out: Suggestion[] = [];
  for (const raw of items) {
    const item = raw as { id?: unknown; name?: unknown; full_name?: unknown; address_name?: unknown; point?: { lat?: unknown; lon?: unknown } };
    const lat = item?.point?.lat; const lon = item?.point?.lon;
    if (typeof lat !== 'number' || typeof lon !== 'number' || !Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    const title = typeof item.name === 'string' && item.name ? item.name : typeof item.full_name === 'string' ? item.full_name : '';
    if (!title) continue;
    const subtitle = typeof item.address_name === 'string' ? item.address_name : '';
    out.push({ id: typeof item.id === 'string' ? item.id : `${lat},${lon}:${title}`, title, subtitle, lat, lon });
  }
  return out;
}

export type SuggesterOptions = {
  key: string;
  onResult: (query: string, items: Suggestion[]) => void;
  onUnavailable: () => void;
  onPending?: (pending: boolean) => void;
  fetchFn?: typeof fetch;
  now?: () => number;
};

/** `input()` is called on every keystroke; at most one request leaves per debounce window and per gap. */
export function createSuggester(opts: SuggesterOptions) {
  const doFetch = opts.fetchFn ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const now = opts.now ?? (() => Date.now());
  const cache = new Map<string, Suggestion[]>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let controller: AbortController | null = null;
  let lastSent = -Infinity;
  let generation = 0;
  let dead = false;

  function cancel(): void {
    generation += 1;
    if (timer) { clearTimeout(timer); timer = null; }
    controller?.abort(); controller = null;
  }
  function fail(): void { dead = true; cancel(); opts.onPending?.(false); opts.onUnavailable(); }

  async function send(query: string, gen: number): Promise<void> {
    controller = new AbortController();
    lastSent = now();
    opts.onPending?.(true);
    try {
      const res = await doFetch(suggestUrl(query, opts.key), { signal: controller.signal });
      if (gen !== generation) return;
      if (!res.ok) { fail(); return; }
      const items = parseSuggestions(await res.json());
      if (gen !== generation) return;
      cache.set(query, items);
      opts.onPending?.(false);
      opts.onResult(query, items);
    } catch (e) {
      if (gen !== generation || (e as { name?: string })?.name === 'AbortError') return;
      fail();
    }
  }

  return {
    input(raw: string): void {
      if (dead) return;
      const query = raw.trim().replace(/\s+/g, ' ');
      cancel();
      opts.onPending?.(false);
      if (query.length < MIN_QUERY) { opts.onResult(query, []); return; }
      const cached = cache.get(query);
      if (cached) { opts.onResult(query, cached); return; }
      const gen = generation;
      const wait = Math.max(DEBOUNCE_MS, lastSent + MIN_GAP_MS - now());
      timer = setTimeout(() => { timer = null; void send(query, gen); }, wait);
    },
    dispose(): void { dead = true; cancel(); },
  };
}
